'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquarePlus,
  RefreshCw,
  Filter,
  ExternalLink,
  Clock,
  MessageCircle,
  Bug,
  Lightbulb,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FeedbackModal } from '@features/feedback/components';
import { PageState } from '@/components/common/feedback/PageState';
import {
  getMyFeedbacks,
  UserFeedback,
  FeedbackType,
  FeedbackStatus,
  getFeedbackTypeLabel,
  getFeedbackStatusLabel,
  getFeedbackStatusColor,
  getFeedbackTypeColor,
  ApiClientError,
} from '@/services/api/feedback';

// 类型图标映射
const TYPE_ICONS = {
  [FeedbackType.BUG]: Bug,
  [FeedbackType.FEATURE]: Lightbulb,
  [FeedbackType.IMPROVEMENT]: TrendingUp,
  [FeedbackType.OTHER]: HelpCircle,
};

/**
 * 我的反馈列表页面
 */
export default function MyFeedbackPage() {
  const { t, locale } = useTranslation();

  // 状态
  const [feedbacks, setFeedbacks] = useState<UserFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // 筛选
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<FeedbackType | ''>('');

  // 弹窗
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);

  // 加载反馈列表
  const loadFeedbacks = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = {
        page,
        pageSize,
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
        sortBy: 'createdAt',
        sortOrder: 'desc' as const,
      };

      const result = await getMyFeedbacks(params);
      setFeedbacks(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.feedback?.error?.loadFailed || 'Failed to load feedback');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, pageSize, statusFilter, typeFilter, t]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  // 刷新
  const handleRefresh = () => {
    loadFeedbacks(true);
  };

  // 重置筛选
  const handleResetFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setPage(1);
  };

  // 新建反馈成功后刷新列表
  const handleFeedbackSuccess = () => {
    setPage(1);
    loadFeedbacks(true);
  };

  // 格式化时间
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const dateLocale = locale === 'zh' ? 'zh-CN' : 'en-US';
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" data-testid="page-title">
              {t.feedback?.myFeedback || 'My Feedback'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t.feedback?.list?.total?.replace('{total}', String(total)) || `${total} feedback(s) in total`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t.feedback?.list?.refresh || t.common?.refresh || 'Refresh'}
            </Button>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <MessageSquarePlus className="w-4 h-4 mr-2" />
              {t.feedback?.form?.submit || 'Submit Feedback'}
            </Button>
          </div>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">{t.feedback?.list?.filter || t.common?.filter || 'Filter'}:</span>
          </div>
          
          {/* 状态筛选 */}
          <select
            name="status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as FeedbackStatus | '');
              setPage(1);
            }}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t.feedback?.list?.allStatus || t.common?.all || 'All'}</option>
            <option value={FeedbackStatus.PENDING}>{t.feedback?.statusPending || 'Pending'}</option>
            <option value={FeedbackStatus.IN_PROGRESS}>{t.feedback?.statusInProgress || 'In Progress'}</option>
            <option value={FeedbackStatus.RESOLVED}>{t.feedback?.statusResolved || 'Resolved'}</option>
            <option value={FeedbackStatus.CLOSED}>{t.feedback?.statusClosed || 'Closed'}</option>
          </select>

          {/* 类型筛选 */}
          <select
            name="type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as FeedbackType | '');
              setPage(1);
            }}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t.feedback?.list?.allTypes || t.common?.all || 'All'}</option>
            <option value={FeedbackType.BUG}>{t.feedback?.typeBug || 'Bug Report'}</option>
            <option value={FeedbackType.FEATURE}>{t.feedback?.typeFeature || 'Feature Request'}</option>
            <option value={FeedbackType.IMPROVEMENT}>{t.feedback?.typeImprovement || 'Improvement'}</option>
            <option value={FeedbackType.OTHER}>{t.feedback?.typeOther || 'Other'}</option>
          </select>

          {(statusFilter || typeFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-gray-500"
            >
              {t.feedback?.list?.reset || t.common?.reset || 'Reset'}
            </Button>
          )}
        </div>
      </div>

      {/* 反馈列表 */}
      <div
        className="bg-white rounded-lg border border-gray-200"
        data-testid="feedback-table"
      >
        {loading ? (
          <div className="p-12 text-center" data-testid="loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t.common?.loading || 'Loading...'}</p>
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="p-12" data-testid="empty-state">
            <PageState
              variant="empty"
              title={t.feedback.list.empty}
              description={t.feedback.list.emptyDesc}
              actionLabel={t.feedback.form.submit}
              onAction={() => setIsModalOpen(true)}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="feedback-list">
            {feedbacks.map((feedback) => {
              const TypeIcon = TYPE_ICONS[feedback.type] || HelpCircle;
              const typeColor = getFeedbackTypeColor(feedback.type);
              const statusColor = getFeedbackStatusColor(feedback.status);

              return (
                <div
                  key={feedback.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedFeedback(feedback)}
                >
                  <div className="flex items-start gap-4">
                    {/* 类型图标 */}
                    <div className={`p-2 rounded-lg ${typeColor.bg}`}>
                      <TypeIcon className={`w-5 h-5 ${typeColor.text}`} />
                    </div>

                    {/* 内容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {feedback.title}
                        </h3>
                        <Badge
                          className={`${statusColor.bg} ${statusColor.text} ${statusColor.border} border`}
                          variant="outline"
                        >
                          {getFeedbackStatusLabel(feedback.status, t)}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        {feedback.content}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(feedback.createdAt)}
                        </span>
                        <span className={`${typeColor.text}`}>
                          {getFeedbackTypeLabel(feedback.type, t)}
                        </span>
                        {feedback.adminReply && (
                          <span className="flex items-center gap-1 text-green-600">
                            <MessageCircle className="w-3.5 h-3.5" />
                            {t.feedback?.hasReply || 'Replied'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 查看详情 */}
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div
            className="p-4 border-t border-gray-100 flex items-center justify-between"
            data-testid="pagination"
          >
            <span className="text-sm text-gray-500">
              {t.common?.currentPage
                ?.replace('{current}', String(page))
                ?.replace('{total}', String(totalPages)) || `Page ${page} / ${totalPages}`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                {t.common?.previousPage || t.common?.previous || 'Previous'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                {t.common?.nextPage || t.common?.next || 'Next'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 反馈详情弹窗 */}
      {selectedFeedback && (
        <FeedbackDetailModal
          feedback={selectedFeedback}
          open={!!selectedFeedback}
          onOpenChange={(open) => !open && setSelectedFeedback(null)}
          t={t}
          locale={locale}
        />
      )}

      {/* 新建反馈弹窗 */}
      <FeedbackModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={handleFeedbackSuccess}
      />
    </div>
  );
}

// 反馈详情弹窗组件
function FeedbackDetailModal({
  feedback,
  open,
  onOpenChange,
  t,
  locale,
}: {
  feedback: UserFeedback;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t: any;
  locale: string;
}) {
  const TypeIcon = TYPE_ICONS[feedback.type] || HelpCircle;
  const typeColor = getFeedbackTypeColor(feedback.type);
  const statusColor = getFeedbackStatusColor(feedback.status);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const dateLocale = locale === 'zh' ? 'zh-CN' : 'en-US';
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}
      onClick={() => onOpenChange(false)}
      data-testid="feedback-detail-modal"
    >
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-black/50" />

      {/* 弹窗内容 */}
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900" data-testid="page-title">
            {t.feedback?.detail?.title || 'Feedback Details'}
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="sr-only">{t.common?.close || 'Close'}</span>
            ✕
          </button>
        </div>

        {/* 内容 */}
        <div className="p-4 space-y-6" data-testid="detail-card">
          {/* 状态和类型 */}
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${typeColor.bg}`}>
              <TypeIcon className={`w-5 h-5 ${typeColor.text}`} />
            </div>
            <Badge
              className={`${statusColor.bg} ${statusColor.text} ${statusColor.border} border`}
              variant="outline"
            >
              {getFeedbackStatusLabel(feedback.status, t)}
            </Badge>
            <span className={`text-sm ${typeColor.text}`}>
              {getFeedbackTypeLabel(feedback.type, t)}
            </span>
          </div>

          {/* 标题 */}
          <div>
            <h3 className="text-xl font-medium text-gray-900">{feedback.title}</h3>
          </div>

          {/* 描述 */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {t.feedback?.form?.content || 'Description'}
            </h4>
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
              {feedback.content}
            </div>
          </div>

          {/* 附件 */}
          {feedback.attachments && feedback.attachments.length > 0 && (
            <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {t.feedback?.detail?.attachments || 'Attachments'}
            </h4>
              <div className="grid grid-cols-2 gap-2">
                {feedback.attachments.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={
                        t.feedback?.detail?.attachmentItem?.replace('{index}', String(index + 1)) ||
                        `Attachment ${index + 1}`
                      }
                      className="rounded-lg border border-gray-200 object-cover aspect-video"
                    />
                ))}
              </div>
            </div>
          )}

          {/* 管理员回复 */}
          {feedback.adminReply && (
            <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-600" />
              {t.feedback?.admin?.adminReply || 'Admin Reply'}
            </h4>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                {feedback.adminReply}
              </div>
            </div>
          )}

          {/* 时间信息 */}
          <div className="pt-4 border-t border-gray-200 space-y-2 text-sm text-gray-500">
            <div className="flex justify-between">
              <span>{t.feedback?.detail?.submitTime || 'Submit Time'}</span>
              <span>{formatDate(feedback.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t.feedback?.detail?.updateTime || 'Update Time'}</span>
              <span>{formatDate(feedback.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
